/**
 * SketchUploadDialog — admin workflow to:
 *   1. Upload a hand-drawn sketch photo
 *   2. AI cleans it (Gemini Nano Banana) — side-by-side preview
 *   3. AI detects markers (GPT-4o Vision) — editable list
 *   4. Confirm → creates FloorPlan + PestDevices
 *
 * Props: open, clientId, clientName, onClose, onSuccess(floorPlanId)
 */
import React, { useRef, useState } from "react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Upload, Sparkles, RefreshCcw, ScanSearch, FileDown, Trash2,
    ChevronRight, ChevronLeft, CheckCircle2, Loader2, AlertTriangle, Maximize2, Crop, Minimize2,
} from "lucide-react";

const DEVICE_TYPE_OPTS = [
    { v: "cebadero_cebo", l: "Cebadero" },
    { v: "trampa", l: "Trampa" },
    { v: "lampara_uv", l: "Lámpara UV" },
];
const ZONE_OPTS = [
    { v: "verde", l: "Verde (perímetro exterior)" },
    { v: "amarilla", l: "Amarilla (puntos focales interiores)" },
    { v: "roja", l: "Roja (áreas de acceso al cliente)" },
];
const TRAP_SUBTYPES = [
    { v: "__none__", l: "(Sin especificar)" },
    { v: "trex", l: "T-Rex" },
    { v: "multicaptura", l: "Multicaptura" },
    { v: "gato_papel", l: "Gato papel" },
    { v: "bandeja", l: "Bandeja" },
];
const BAIT_SUBTYPES = [
    { v: "__none__", l: "(Sin especificar)" },
    { v: "caja", l: "Cebadero de caja" },
    { v: "tubo", l: "Cebadero de tubo" },
    { v: "roca", l: "Cebadero roca" },
];

const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = reject;
        r.onload = () => {
            const s = r.result;
            const idx = s.indexOf(",");
            resolve(idx >= 0 ? s.slice(idx + 1) : s);
        };
        r.readAsDataURL(file);
    });

export default function SketchUploadDialog({ open, clientId, branchId, clientName, defaultPlanName, onClose, onSuccess }) {
    // Step: upload (0) → review (1) → crop (1.5) → markers (2)
    const [step, setStep] = useState(0);
    const [planName, setPlanName] = useState("");
    const [busy, setBusy] = useState(false);
    const [sketchId, setSketchId] = useState(null);
    const [origUrl, setOrigUrl] = useState(null);
    const [cleanedUrl, setCleanedUrl] = useState(null);
    const [useCleaned, setUseCleaned] = useState(true);
    const [markers, setMarkers] = useState([]);
    const [areas, setAreas] = useState([]);
    const [busyMsg, setBusyMsg] = useState("");
    const [cropping, setCropping] = useState(false);
    const [cropBox, setCropBox] = useState(null); // {x, y, w, h} in % of image
    const [expanded, setExpanded] = useState(false);
    const cropImgRef = useRef(null);
    const cropDragRef = useRef(null);
    const fileRef = useRef(null);

    const reset = () => {
        setStep(0);
        setPlanName(defaultPlanName || "Plano del técnico");
        setBusy(false);
        setSketchId(null);
        setOrigUrl(null);
        setCleanedUrl(null);
        setUseCleaned(true);
        setMarkers([]);
        setAreas([]);
        setCropping(false);
        setCropBox(null);
    };

    const close = () => { reset(); onClose?.(); };

    // Update plan name when dialog opens with a new default
    React.useEffect(() => {
        if (open && defaultPlanName) setPlanName(defaultPlanName);
    }, [open, defaultPlanName]);

    const downloadLegend = () => {
        const token = localStorage.getItem("trulyn_token");
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/sketches/legend`;
        // Use fetch + blob to include auth header
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const u = URL.createObjectURL(b);
                const a = document.createElement("a");
                a.href = u;
                a.download = "TRULYN_leyenda_bosquejo.pdf";
                a.click();
                URL.revokeObjectURL(u);
            });
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Máx 10MB por imagen");
            return;
        }
        setBusy(true);
        setBusyMsg("Enviando imagen a la IA...");
        try {
            const b64 = await fileToBase64(file);
            const r = await api.post("/sketches/clean", {
                image_base64: b64,
                client_id: clientId,
                plan_name: planName,
            }, { timeout: 30000 });
            const sid = r.data.sketch_id;
            setSketchId(sid);
            setOrigUrl(r.data.original_url);
            // Poll for completion
            setBusyMsg("Procesando con IA... esto puede tomar 30-120 segundos");
            const poll = async () => {
                for (let i = 0; i < 60; i++) {
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    try {
                        const s = await api.get(`/sketches/status/${sid}`);
                        if (s.data.status === "cleaned") {
                            setCleanedUrl(s.data.cleaned_url);
                            setStep(1);
                            toast.success("Plano generado por IA");
                            return;
                        }
                        if (s.data.status === "error") {
                            toast.error(s.data.error || "Error al procesar la imagen");
                            return;
                        }
                        const labels = { processing_replicate: "Replicate", processing_emergent: "Emergent", processing_gemini: "Google AI Studio" };
                        const label = labels[s.data.status] || "IA";
                        setBusyMsg(`Procesando con ${label}... ${(i + 1) * 3}s`);
                    } catch { /* continue polling */ }
                }
                toast.error("Timeout: la IA tardó demasiado. Intenta de nuevo.");
            };
            await poll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo enviar la imagen");
        } finally {
            setBusy(false);
            setBusyMsg("");
        }
    };

    const regenerate = async () => {
        if (!sketchId) return;
        setBusy(true);
        setBusyMsg("Regenerando plano con IA...");
        try {
            const r = await api.post("/sketches/regenerate", { sketch_id: sketchId }, { timeout: 30000 });
            const sid = r.data.sketch_id;
            setSketchId(sid);
            // Poll for completion (same as initial upload)
            setBusyMsg("Procesando con IA... esto puede tomar 30-120 segundos");
            for (let i = 0; i < 60; i++) {
                await new Promise(resolve => setTimeout(resolve, 3000));
                try {
                    const s = await api.get(`/sketches/status/${sid}`);
                    if (s.data.status === "cleaned") {
                        setCleanedUrl(s.data.cleaned_url);
                        toast.success("Plano regenerado");
                        setBusy(false);
                        setBusyMsg("");
                        return;
                    }
                    if (s.data.status === "error") {
                        toast.error(s.data.error || "Error al regenerar");
                        setBusy(false);
                        setBusyMsg("");
                        return;
                    }
                    const label = s.data.status === "processing_gemini" ? "Gemini (fallback)" : "Replicate";
                    setBusyMsg(`Regenerando con ${label}... ${(i + 1) * 3}s`);
                } catch { /* continue polling */ }
            }
            toast.error("Timeout: la IA tardó demasiado. Intenta de nuevo.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo regenerar");
        } finally {
            setBusy(false);
            setBusyMsg("");
        }
    };

    const detect = async () => {
        if (!sketchId) return;
        setBusy(true);
        setBusyMsg("Detectando áreas con GPT-4o Vision... (≈30-60s)");
        try {
            const r = await api.post("/sketches/detect", { sketch_id: sketchId, use_original: true }, { timeout: 180000 });
            setAreas(r.data.areas || []);
            setStep(2);
            toast.success(`${r.data.areas?.length || 0} áreas detectadas`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo detectar áreas");
        } finally {
            setBusy(false);
            setBusyMsg("");
        }
    };

    const detectDevices = async () => {
        if (!sketchId) return;
        setBusy(true);
        setBusyMsg("Detectando dispositivos con GPT-4o Vision... (≈30-60s)");
        try {
            const r = await api.post("/sketches/detect", { sketch_id: sketchId, use_original: true }, { timeout: 180000 });
            setMarkers(r.data.markers || []);
            toast.success(`${r.data.markers?.length || 0} dispositivos detectados`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo detectar dispositivos");
        } finally {
            setBusy(false);
            setBusyMsg("");
        }
    };

    const startCrop = () => {
        setCropping(true);
        setCropBox({ x: 0, y: 0, w: 100, h: 80 }); // default: full width, 80% height (cuts bottom)
    };

    const handleCropPointerDown = (e) => {
        const img = cropImgRef.current;
        if (!img) return;
        const rect = img.getBoundingClientRect();
        const startX = ((e.clientX - rect.left) / rect.width) * 100;
        const startY = ((e.clientY - rect.top) / rect.height) * 100;
        cropDragRef.current = { startX, startY, moved: false };
        const onMove = (ev) => {
            const curX = ((ev.clientX - rect.left) / rect.width) * 100;
            const curY = ((ev.clientY - rect.top) / rect.height) * 100;
            cropDragRef.current.moved = true;
            const x = Math.max(0, Math.min(startX, curX));
            const y = Math.max(0, Math.min(startY, curY));
            const w = Math.min(100 - x, Math.abs(curX - startX));
            const h = Math.min(100 - y, Math.abs(curY - startY));
            setCropBox({ x, y, w, h });
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    const applyCrop = async () => {
        if (!cropBox || !sketchId) return;
        const selectedUrl = useCleaned ? cleanedUrl : origUrl;
        if (!selectedUrl) return;
        setBusy(true);
        setBusyMsg("Recortando...");
        try {
            // Get natural image dimensions
            const img = new Image();
            img.crossOrigin = "anonymous";
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = assetUrl(selectedUrl);
            });
            const natW = img.naturalWidth;
            const natH = img.naturalHeight;
            const px = { x: (cropBox.x / 100) * natW, y: (cropBox.y / 100) * natH, w: (cropBox.w / 100) * natW, h: (cropBox.h / 100) * natH };
            // Draw cropped to canvas and get base64
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(px.w);
            canvas.height = Math.round(px.h);
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, px.x, px.y, px.w, px.h, 0, 0, px.w, px.h);
            const croppedB64 = canvas.toDataURL("image/png");
            // Upload cropped as new sketch image
            const r = await api.post("/sketches/replace-image", {
                sketch_id: sketchId,
                image_base64: croppedB64,
                target: useCleaned ? "cleaned" : "original",
            });
            if (useCleaned) setCleanedUrl(r.data.url);
            else setOrigUrl(r.data.url);
            setCropping(false);
            setCropBox(null);
            toast.success("Imagen recortada");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al recortar");
        } finally {
            setBusy(false);
            setBusyMsg("");
        }
    };


    const addManualMarker = () => {
        setMarkers((m) => [...m, {
            device_type: "trampa", zone: "roja", trap_subtype: "trex", label: "", position_x: 50, position_y: 50,
        }]);
    };
    const removeMarker = (idx) => setMarkers((m) => m.filter((_, i) => i !== idx));
    const updateMarker = (idx, patch) => setMarkers((m) => m.map((x, i) => i === idx ? { ...x, ...patch } : x));

    const addManualArea = () => {
        setAreas((a) => [...a, { name: "Nueva área", x: 30, y: 30, w: 25, h: 20 }]);
    };
    const removeArea = (idx) => setAreas((a) => a.filter((_, i) => i !== idx));
    const updateArea = (idx, patch) => setAreas((a) => a.map((x, i) => i === idx ? { ...x, ...patch } : x));

    const commit = async () => {
        if (!sketchId) return;
        setBusy(true);
        setBusyMsg("Creando plano y dispositivos...");
        try {
            const r = await api.post("/sketches/commit", {
                sketch_id: sketchId,
                client_id: clientId,
                branch_id: branchId || null,
                plan_name: planName || "Plano del técnico",
                use_cleaned: useCleaned,
                markers,
                areas,
            });
            toast.success(`Plano creado · ${r.data.devices_created} dispositivos · ${areas.length} áreas`);
            onSuccess?.(r.data.floor_plan_id);
            close();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo guardar el plano");
        } finally {
            setBusy(false);
            setBusyMsg("");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && !busy && close()}>
            <DialogContent className={`${expanded ? "max-w-[95vw] w-[95vw] max-h-[95vh]" : "max-w-4xl max-h-[92vh]"} overflow-y-auto transition-all`} data-testid="sketch-upload-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#FFD62B]" />
                        Subir bosquejo del técnico {clientName ? `· ${clientName}` : ""}
                        <div className="flex-1" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setExpanded(!expanded)} title={expanded ? "Reducir" : "Expandir"}>
                            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </Button>
                    </DialogTitle>
                </DialogHeader>

                {/* Stepper */}
                <div className="flex items-center gap-2 text-xs">
                    {["Subir foto", "Revisar plano", "Áreas y dispositivos"].map((s, i) => (
                        <React.Fragment key={s}>
                            <div className={`px-2 py-1 rounded ${step === i ? "bg-[hsl(var(--trulyn-navy))] text-white font-semibold" : step > i ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                                {step > i && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                                {i + 1}. {s}
                            </div>
                            {i < 2 && <ChevronRight className="w-3 h-3 text-slate-300" />}
                        </React.Fragment>
                    ))}
                </div>

                {busy && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2.5 flex items-center gap-2 text-sm text-blue-900" data-testid="sketch-busy">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {busyMsg || "Procesando..."}
                    </div>
                )}

                {/* STEP 0 — Upload */}
                {step === 0 && (
                    <div className="space-y-3 pt-2">
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                                <b>Para mejor calidad:</b> usa la plantilla TRULYN. Imprímela, dibuja el plano con la leyenda estándar (■ □ ▲ ★) y luego sube la foto aquí.
                                <Button
                                    type="button"
                                    variant="link"
                                    onClick={downloadLegend}
                                    className="text-amber-900 underline px-1 py-0 h-auto"
                                    data-testid="download-legend-btn"
                                >
                                    <FileDown className="w-3 h-3 mr-1 inline" />
                                    Descargar plantilla TRULYN (PDF)
                                </Button>
                            </div>
                        </div>
                        <div>
                            <Label>Nombre del plano</Label>
                            <Input
                                value={planName}
                                onChange={(e) => setPlanName(e.target.value)}
                                placeholder="Ej: Cocina principal, Bodega externa..."
                                data-testid="sketch-plan-name"
                            />
                        </div>
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFile}
                                disabled={busy}
                                className="hidden"
                                data-testid="sketch-file-input"
                            />
                            <Button
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                                className="w-full bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black"
                                data-testid="sketch-upload-btn"
                            >
                                <Upload className="w-4 h-4 mr-1" /> Subir foto del bosquejo
                            </Button>
                            <p className="text-[11px] text-muted-foreground mt-1.5 text-center">
                                JPG / PNG · máx 10MB · la IA tarda ≈10-30s
                            </p>
                        </div>
                    </div>
                )}

                {/* STEP 1 — Review (antes/después) + optional crop */}
                {step === 1 && !cropping && (
                    <div className="space-y-3 pt-2">
                        <div className="grid md:grid-cols-2 gap-3">
                            <Card className="p-2">
                                <div className="text-xs font-semibold text-muted-foreground mb-1">Original (bosquejo)</div>
                                {origUrl && <img src={assetUrl(origUrl)} alt="Original" className={`w-full ${expanded ? "max-h-[70vh]" : "max-h-80"} object-contain rounded`} data-testid="sketch-original-img" />}
                            </Card>
                            <Card className="p-2 ring-2 ring-[#FFD62B]/50">
                                <div className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                                    <Sparkles className="w-3 h-3 text-[#FFD62B]" /> Limpiado por IA
                                </div>
                                {cleanedUrl && <img src={assetUrl(cleanedUrl)} alt="Limpio" className={`w-full ${expanded ? "max-h-[70vh]" : "max-h-80"} object-contain rounded bg-white`} data-testid="sketch-cleaned-img" />}
                            </Card>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={regenerate} disabled={busy} data-testid="sketch-regenerate-btn">
                                <RefreshCcw className="w-4 h-4 mr-1" /> Regenerar
                            </Button>
                            <Button variant="outline" onClick={startCrop} disabled={busy} data-testid="sketch-crop-btn">
                                <Maximize2 className="w-4 h-4 mr-1" /> Recortar
                            </Button>
                            <Button variant="ghost" onClick={() => setStep(0)} disabled={busy}>
                                <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
                            </Button>
                            <div className="flex-1" />
                            <Button variant="outline" onClick={() => { setAreas([]); setMarkers([]); setStep(2); }} disabled={busy} data-testid="sketch-skip-detect-btn">
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Guardar sin detectar
                            </Button>
                            <Button onClick={detect} disabled={busy} className="bg-[hsl(var(--trulyn-navy))] hover:bg-[hsl(var(--trulyn-navy))]/90 text-white" data-testid="sketch-detect-btn">
                                <ScanSearch className="w-4 h-4 mr-1" /> Detectar áreas
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 1.5 — Crop view */}
                {step === 1 && cropping && (
                    <div className="space-y-3 pt-2">
                        <div className="text-xs bg-yellow-50 border border-yellow-300 text-yellow-900 rounded p-2">
                            Arrastra sobre la imagen para seleccionar el área que quieres conservar. Todo lo que quede fuera del rectángulo se eliminará.
                        </div>
                        <div className="relative inline-block w-full select-none" style={{ cursor: "crosshair" }} onPointerDown={handleCropPointerDown}>
                            <img
                                ref={cropImgRef}
                                src={assetUrl(useCleaned ? cleanedUrl : origUrl)}
                                alt="Crop"
                                className="w-full rounded border"
                                draggable={false}
                            />
                            {cropBox && (
                                <>
                                    {/* Dark overlay outside crop area */}
                                    <div className="absolute inset-0 pointer-events-none" style={{ background: "rgba(0,0,0,0.45)" }}>
                                        <div style={{
                                            position: "absolute",
                                            left: `${cropBox.x}%`, top: `${cropBox.y}%`,
                                            width: `${cropBox.w}%`, height: `${cropBox.h}%`,
                                            background: "transparent",
                                            boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                                            border: "2px dashed #FFD62B",
                                        }} />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="ghost" onClick={() => { setCropping(false); setCropBox(null); }}>
                                <ChevronLeft className="w-4 h-4 mr-1" /> Cancelar
                            </Button>
                            <div className="flex-1" />
                            <Button onClick={applyCrop} disabled={busy || !cropBox} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" data-testid="sketch-apply-crop">
                                {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Recortando...</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Aplicar recorte</>}
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 2 — Markers + Areas editor */}
                {step === 2 && (
                    <div className="space-y-4 pt-2">
                        {/* AREAS section */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold">
                                    Áreas detectadas: <b>{areas.length}</b>
                                    <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                                        (puedes renombrarlas después con &quot;Nombrar área&quot;)
                                    </span>
                                </span>
                                <Button variant="outline" size="sm" onClick={addManualArea} data-testid="sketch-add-area-btn">
                                    + Agregar área
                                </Button>
                            </div>
                            {areas.length === 0 ? (
                                <Card className="p-3 text-center border-dashed text-muted-foreground text-xs">
                                    Sin áreas detectadas. Puedes agregar manualmente o saltar este paso.
                                </Card>
                            ) : (
                                <div className="space-y-1.5 max-h-[24vh] overflow-y-auto" data-testid="sketch-areas-list">
                                    {areas.map((a, idx) => (
                                        <Card key={idx} className="p-2" data-testid={`sketch-area-${idx}`}>
                                            <div className="grid grid-cols-12 gap-2 items-end">
                                                <div className="col-span-1">
                                                    <Badge variant="outline" className="font-mono text-[10px]">A{idx + 1}</Badge>
                                                </div>
                                                <div className="col-span-5">
                                                    <Label className="text-[10px]">Nombre del área</Label>
                                                    <Input className="h-8 text-xs" value={a.name}
                                                           onChange={(e) => updateArea(idx, { name: e.target.value })}
                                                           data-testid={`sketch-area-name-${idx}`} />
                                                </div>
                                                <div className="col-span-1"><Label className="text-[10px]">X%</Label>
                                                    <Input type="number" min={0} max={100} step={0.1} className="h-8 text-xs" value={a.x}
                                                           onChange={(e) => updateArea(idx, { x: parseFloat(e.target.value) || 0 })} /></div>
                                                <div className="col-span-1"><Label className="text-[10px]">Y%</Label>
                                                    <Input type="number" min={0} max={100} step={0.1} className="h-8 text-xs" value={a.y}
                                                           onChange={(e) => updateArea(idx, { y: parseFloat(e.target.value) || 0 })} /></div>
                                                <div className="col-span-1"><Label className="text-[10px]">W%</Label>
                                                    <Input type="number" min={1} max={100} step={0.1} className="h-8 text-xs" value={a.w}
                                                           onChange={(e) => updateArea(idx, { w: parseFloat(e.target.value) || 1 })} /></div>
                                                <div className="col-span-1"><Label className="text-[10px]">H%</Label>
                                                    <Input type="number" min={1} max={100} step={0.1} className="h-8 text-xs" value={a.h}
                                                           onChange={(e) => updateArea(idx, { h: parseFloat(e.target.value) || 1 })} /></div>
                                                <div className="col-span-2 flex justify-end">
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600"
                                                            onClick={() => removeArea(idx)}
                                                            data-testid={`sketch-area-remove-${idx}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* MARKERS / DEVICES section */}
                        <div className="border-t pt-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold">
                                    Dispositivos: <b>{markers.length}</b>
                                </span>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={detectDevices} disabled={busy} data-testid="sketch-detect-devices-btn">
                                        <ScanSearch className="w-3 h-3 mr-1" /> Detectar dispositivos
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={addManualMarker} data-testid="sketch-add-marker-btn">
                                        + Agregar
                                    </Button>
                                </div>
                            </div>
                            {markers.length === 0 ? (
                                <Card className="p-3 text-center border-dashed text-muted-foreground text-xs">
                                    Sin dispositivos. Usa "Detectar dispositivos" o agrega manualmente.
                                </Card>
                            ) : (
                                <div className="space-y-1.5 max-h-[24vh] overflow-y-auto" data-testid="sketch-markers-list">
                                    {markers.map((m, idx) => {
                                        const subtypes = m.device_type === "trampa" ? TRAP_SUBTYPES : m.device_type === "cebadero_cebo" ? BAIT_SUBTYPES : null;
                                        return (
                                            <Card key={idx} className="p-2" data-testid={`sketch-marker-${idx}`}>
                                                <div className="grid grid-cols-12 gap-2 items-end">
                                                    <div className="col-span-1">
                                                        <Badge variant="outline" className="font-mono text-[10px]">#{idx + 1}</Badge>
                                                    </div>
                                                    <div className="col-span-3">
                                                        <Label className="text-[10px]">Tipo</Label>
                                                        <Select value={m.device_type} onValueChange={(v) => updateMarker(idx, { device_type: v, trap_subtype: v === "trampa" ? "trex" : v === "cebadero_cebo" ? "caja" : "" })}>
                                                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                                            <SelectContent>{DEVICE_TYPE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <Label className="text-[10px]">Zona</Label>
                                                        <Select value={m.zone} onValueChange={(v) => updateMarker(idx, { zone: v })}>
                                                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                                            <SelectContent>{ZONE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="col-span-3">
                                                        <Label className="text-[10px]">{subtypes ? "Subtipo" : "—"}</Label>
                                                        {subtypes ? (
                                                            <Select value={m.trap_subtype || "__none__"} onValueChange={(v) => updateMarker(idx, { trap_subtype: v === "__none__" ? "" : v })}>
                                                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                                                                <SelectContent>{subtypes.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                                                            </Select>
                                                        ) : (
                                                            <Input disabled value="" className="h-8 text-xs" />
                                                        )}
                                                    </div>
                                                    <div className="col-span-2">
                                                        <Label className="text-[10px]">Pos X/Y</Label>
                                                        <div className="flex gap-1">
                                                            <Input type="number" min={0} max={100} className="h-8 text-xs w-12" value={m.position_x?.toFixed(0)} onChange={(e) => updateMarker(idx, { position_x: parseFloat(e.target.value) || 0 })} />
                                                            <Input type="number" min={0} max={100} className="h-8 text-xs w-12" value={m.position_y?.toFixed(0)} onChange={(e) => updateMarker(idx, { position_y: parseFloat(e.target.value) || 0 })} />
                                                        </div>
                                                    </div>
                                                    <div className="col-span-1 flex justify-end">
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeMarker(idx)} data-testid={`sketch-marker-remove-${idx}`}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button variant="ghost" onClick={() => setStep(1)} disabled={busy}>
                                <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
                            </Button>
                            <div className="flex-1" />
                            <Button onClick={commit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sketch-commit-btn">
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar y crear plano
                            </Button>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={close} disabled={busy}>Cerrar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
